import React, { useEffect, useState, useMemo } from "react";
import { formatedDate } from "../../../utils";
import MaterialReactTable, {
  MRT_ColumnDef,
  MRT_RowSelectionState,
} from "material-react-table";
import ReactQuillComponent from "../../../components/ReactQuillComponent";
import { Button, Tooltip } from "@fluentui/react-components";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Eye16Filled,
  EyeFilled,
  MoreHorizontal24Filled,
  Edit16Filled,
  Dismiss24Regular,
} from "@fluentui/react-icons";
import ASCButton from "../../../components/Button";
import {
  findRowIndexByKey,
  getContextIdKey,
  getRowKey,
  useRestoreRowSelection,
} from "../../../components/tableRowSelection";

const tableColumnList = {
  Vulnerbality: [
    {
      accessorKey: "vulnerability_date",
      header: "Date",
      Cell: ({ row }: any) => (
        <>{formatedDate(row.original.vulnerability_date)}</>
      ),
    },
    {
      accessorKey: "driver",
      header: "Driver",
    },
    {
      accessorKey: "characteristics",
      header: "Characteristics",
    },
    {
      accessorKey: "note_text",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
    {
      accessorKey: "ratings",
      header: "Score",
    },
    {
      accessorKey: "status",
      header: "Status",
      Cell: ({ row }: any) => (
        <>
          {row.original.status === "0" || row.original.status === 0
            ? "Active"
            : "Inactive"}
        </>
      ),
    },
  ],
};

const VulnerabilityTable = (props: any) => {
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const { clientId, action, selectedItemId } = useParams();
  const tableRef = React.useRef<HTMLDivElement>(null);
  // Set while the selection is being restored from the URL, so the effect
  // below can tell it apart from a user clicking a row.
  const restoringSelectionRef = React.useRef(false);
  const idKey = getContextIdKey(props.context) ?? "vulnerability_id";
  const navigate = useNavigate();
  useEffect(() => {
    // A selection restored from the URL must not re-navigate: it would rewrite
    // an `/edit` URL back to `/view` and push a history entry on every refresh.
    if (restoringSelectionRef.current) {
      restoringSelectionRef.current = false;
      return;
    }
    if (props.data.length > 0 && Object.keys(rowSelection)[0] !== undefined) {
      const index = findRowIndexByKey(
        props.data,
        Object.keys(rowSelection)[0],
        idKey,
      );
      const selectedRow = index < 0 ? undefined : props.data[index];
      if (selectedRow) {
        if (props.module != undefined && props.module == "partner") {
          navigate(
            `/clients/${clientId}/partner/${props.section}/${selectedRow.vulnerability_id}/view`
          );
        } else {
          navigate(
            `/clients/${clientId}/${props.section}/${selectedRow.vulnerability_id}/view`
          );
        }
      }
    }
  }, [rowSelection]);

  // Previously this clamped a `findIndex` miss to row 0 (selecting the first
  // record whenever the URL id was not in the list) and wrote a "-1" key,
  // which highlights nothing and reports "Record 0 of N".
  useRestoreRowSelection({
    data: props.data,
    idKey,
    selectedId: selectedItemId,
    rowSelection,
    setRowSelection,
    setCurrentRecord: props.setCurrentRecord,
    onBeforeRestore: () => {
      restoringSelectionRef.current = true;
    },
    containerRef: tableRef,
  });

  return (
    <div ref={tableRef}>
      <MaterialReactTable
        columns={tableColumnList.Vulnerbality}
        data={props.data}
        enableRowSelection
        enableColumnActions={false}
        onRowSelectionChange={setRowSelection}
        enablePagination={false}
        // enableRowNumbers
        enableBottomToolbar={false}
        rowNumberMode="static"
        enableMultiRowSelection={false}
        muiTableBodyRowProps={({ row }) => ({
          onClick: row.getToggleSelectedHandler(),
          sx: { cursor: "pointer" },
        })}
        getRowId={(row: any, index: number) => getRowKey(row, index, idKey)}
        enableRowActions={false}
        displayColumnDefOptions={{
          "mrt-row-actions": {
            header: "Actions", //change header text
            // size: 300, //make actions column wider
            muiTableHeadCellProps: {
              align: "center",
            },
            muiTableBodyCellProps: {
              align: "center",
            },
          },
        }}
        renderRowActions={({ row, table }) => (
          <div className="table-wraper">
            <Tooltip content="View" relationship="label" positioning="below">
              <ASCButton
                shape="rounded"
                appearance="subtle"
                icon={
                  <Eye16Filled
                    primaryFill="var(--button-color)"
                    onClick={() => {
                      props.handleRowAction("view", row);
                    }}
                  />
                }
              />
            </Tooltip>
            <Tooltip content="Edit" relationship="label" positioning="below">
              <ASCButton
                shape="rounded"
                appearance="subtle"
                icon={
                  <Edit16Filled
                    primaryFill="var(--button-color)"
                    onClick={() => {
                      props.handleRowAction("edit", row);
                    }}
                  />
                }
              />
            </Tooltip>
          </div>
        )}
        initialState={{
          columnPinning: { right: ["mrt-row-actions"] },
        }}
        positionActionsColumn="last"
        positionToolbarAlertBanner="bottom"
        state={{ rowSelection }}
      />
    </div>
  );
};

export default VulnerabilityTable;
